import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CatalogButton from "../../components/CatalogButton/CatalogButton";
import ProductView from "../../components/ProductView/ProductView";
import SelectMacroButton from "../../components/SelectMacroButton/SelectMacroButton";
import SelectorCategory from "../../components/SelectorCategory/SelectorCategory";
import SelectorProduct from "../../components/SelectorProduct/SelectorProduct";
import SkeletonCatalogo from "../../components/SkeletonCatalogo/SkeletonCatalogo";
import {
  useGetAllMacros,
  useGetCategoriesById,
  useGetProductsById,
  useGetProductsDetailsById,
  useGetSubcategoriesById,
} from "../../hooks/react-query";
import styles from "./Catalogo.module.css";
import useCatalogFn from "../../hooks/catalog/useCatalogFn";

function Catalogo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = {
    macro: searchParams.get("macro"),
    cat: searchParams.get("cat"),
    sub: searchParams.get("sub"),
    prod: searchParams.get("prod"),
  };
  const { data: dataMacros, isLoading: isLoadingMacro } = useGetAllMacros();
  const { data: dataCategories, isLoading: isLoadingCat } =
    useGetCategoriesById({
      id: queryParams.macro,
      enabledFn: Boolean(queryParams.macro),
    });
  const { data: dataSubcategories, isLoading: isLoadingSub } =
    useGetSubcategoriesById({
      id: queryParams.cat,
      enabledFn: Boolean(queryParams.cat),
    });
  const { data: dataProducts, isLoading: isLoadingProd } = useGetProductsById({
    id: queryParams.sub,
    enabledFn: Boolean(queryParams.sub),
  });
  const { data: dataProductDetail, isLoading: isLoadingProdDetail } =
    useGetProductsDetailsById({
      id: queryParams.prod,
      enabledFn: Boolean(queryParams.prod),
    });

  const {
    currentSelected,
    disableBtns,
    handleCatBtn,
    handleMacroBtn,
    handleProdBtn,
    handleSubBtn,
    selectedBtns,
    unselectMacro,
    unselectCat,
    unselectProd,
    unselectSub,
    setSelectedBtns,
    setSelectedProductIndex,
    setDisableBtns,
    setSelectedSubcategoryIndex,
    setSelectedCategoryIndex,
    setSelectedMacrocategoryIndex,
    setCurrentSelected,
    currentStep,
  } = useCatalogFn({ searchParams, setSearchParams, dataProductDetail });

  const isLoading = useMemo(() => {
    return (
      isLoadingMacro ||
      isLoadingCat ||
      isLoadingSub ||
      isLoadingProd ||
      isLoadingProdDetail
    );
  }, [
    isLoadingCat,
    isLoadingMacro,
    isLoadingProd,
    isLoadingProdDetail,
    isLoadingSub,
  ]);

  useEffect(() => {
    if (Boolean(queryParams.prod)) {
      setSelectedBtns((prev) => ({
        ...prev,
        prod: true,
      }));
      setSelectedBtns((__) => ({
        macro: true,
        cat: true,
        sub: true,
        prod: true,
      }));
      setDisableBtns((__) => ({
        macro: false,
        cat: false,
        sub: false,
        prod: false,
      }));
      setSelectedProductIndex(queryParams.prod);
      setSelectedSubcategoryIndex(dataProductDetail?.sottocategoriaId);
      setSelectedCategoryIndex(dataProductDetail?.categoriaId);
      setSelectedMacrocategoryIndex(dataProductDetail?.macrocategoriaId);

      setCurrentSelected((__) => ({
        macro: dataProductDetail?.macrocategoria,
        cat: dataProductDetail?.categoria,
        sub: dataProductDetail?.sottocategoria,
        prod: dataProductDetail?.titolo,
      }));
    } else if (Boolean(queryParams.sub)) {
      setSelectedBtns((prev) => ({
        ...prev,
        sub: true,
      }));
      setSelectedBtns((__) => ({
        macro: true,
        cat: true,
        sub: true,
        prod: false,
      }));
      setDisableBtns((__) => ({
        macro: false,
        cat: false,
        sub: false,
        prod: false,
      }));
      setSelectedSubcategoryIndex(queryParams.sub);
      const selected = dataProducts?.find(
        (el) => el.sottocategoriaId === queryParams.sub
      );
      setSelectedCategoryIndex(selected?.categoriaId);
      setSelectedMacrocategoryIndex(selected?.macrocategoriaId);

      setCurrentSelected((prev) => ({
        ...prev,
        macro: selected?.macrocategoria,
        cat: selected?.categoria,
        sub: selected?.sottocategoria,
      }));
    } else if (Boolean(queryParams.cat)) {
      setSelectedBtns((prev) => ({
        ...prev,
        cat: true,
      }));
      setSelectedBtns((__) => ({
        macro: true,
        cat: true,
        sub: false,
        prod: false,
      }));
      setDisableBtns((__) => ({
        macro: false,
        cat: false,
        sub: false,
        prod: true,
      }));
      setSelectedCategoryIndex(queryParams.cat);
      const selected = dataSubcategories?.find(
        (el) => el.categoriaId === queryParams.cat
      );
      setSelectedMacrocategoryIndex(selected?.macrocategoriaId);

      setCurrentSelected((prev) => ({
        ...prev,
        macro: selected?.macrocategoria,
        cat: selected?.categoria,
      }));
    } else if (queryParams.macro) {
      setSelectedBtns((prev) => ({
        ...prev,
        macro: true,
      }));
      setSelectedBtns((__) => ({
        macro: true,
        cat: false,
        sub: false,
        prod: false,
      }));
      setDisableBtns((__) => ({
        macro: false,
        cat: false,
        sub: true,
        prod: true,
      }));
      setSelectedMacrocategoryIndex(queryParams.macro);
      const selected = dataMacros?.find(
        (el) => el.idNodo === queryParams.macro
      );
      console.log({ selected });
      setCurrentSelected((prev) => ({ ...prev, macro: selected?.titolo }));
    }
  }, [
    dataMacros,
    dataProductDetail?.categoria,
    dataProductDetail?.categoriaId,
    dataProductDetail?.macrocategoria,
    dataProductDetail?.macrocategoriaId,
    dataProductDetail?.sottocategoria,
    dataProductDetail?.sottocategoriaId,
    dataProductDetail?.titolo,
    dataProducts,
    dataSubcategories,
    queryParams.cat,
    queryParams.macro,
    queryParams.prod,
    queryParams.sub,
    setCurrentSelected,
    setDisableBtns,
    setSelectedBtns,
    setSelectedCategoryIndex,
    setSelectedMacrocategoryIndex,
    setSelectedProductIndex,
    setSelectedSubcategoryIndex,
  ]);

  const getOptionsTitleLabel = () => {
    switch (currentStep) {
      case "Macrocategoria":
        return "Seleziona una macrocategoria";
      case "Categoria":
        return "Seleziona una categoria";
      case "Sottocategoria":
        return "Seleziona una sottocategoria";
      case "Prodotto":
        return "Seleziona un prodotto";
      default:
        return "Prodotto";
    }
  };

  return (
    <div className={`container ${styles["height-full"]}`}>
      <div className={styles["main-content"]}>
        <div className={`${styles["inner-container"]} col-4`}>
          <span className={styles["title-span"]}>Fai la tua selezione</span>
          <div className={`${styles["btns-col"]} ${styles["box"]}`}>
            <CatalogButton
              id="btnMacro"
              title="Macrocategoria"
              onClick={unselectMacro}
              disabled={false}
              selected={selectedBtns.macro}
              desc={
                selectedBtns.macro ? currentSelected.macro : "Fai una scelta"
              }
            />
            <CatalogButton
              id="btnCat"
              title="Categoria"
              onClick={unselectCat}
              disabled={disableBtns.cat}
              selected={selectedBtns.cat}
              desc={selectedBtns.cat ? currentSelected.cat : "Fai una scelta"}
            />
            <CatalogButton
              id="btnSub"
              title="Sottocategoria"
              onClick={unselectSub}
              disabled={disableBtns.sub}
              selected={selectedBtns.sub}
              desc={selectedBtns.sub ? currentSelected.sub : "Fai una scelta"}
            />
            <CatalogButton
              id="btnProd"
              title="Prodotto"
              onClick={unselectProd}
              disabled={disableBtns.prod}
              selected={selectedBtns.prod}
              desc={selectedBtns.prod ? currentSelected.prod : "Fai una scelta"}
            />
          </div>
        </div>
        <div className={`${styles["inner-container"]} col-8`}>
          <span className={styles["title-span"]}>
            {getOptionsTitleLabel()}
          </span>
          <div className={`${styles["container-col"]} ${styles["box"]}`}>
            {!selectedBtns.macro && (
              <>
                {isLoading && <SkeletonCatalogo />}

                {!isLoading && (
                  <div className={styles["view"]}>
                    <div className={styles["macro-view"]}>
                      {dataMacros?.map((cat, i) => (
                        <SelectMacroButton
                          key={cat.idNodo}
                          index={cat.idNodo}
                          onClick={(e) => handleMacroBtn(e, cat)}
                          // selected={selectedMacrocategoryIndex === searchParams.get("macro")}
                          selected={false}
                          title={cat.titolo}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {selectedBtns.macro && !selectedBtns.cat && (
              <>
                {isLoading ? (
                  <SkeletonCatalogo />
                ) : (
                  <div className={styles["view"]}>
                    <div className={styles["macro-view"]}>
                      {dataCategories
                        ?.sort((a, b) => a.titolo.localeCompare(b.titolo))
                        .map((cat, i) => (
                          <SelectMacroButton
                            key={cat.idNodo}
                            index={cat.idNodo}
                            onClick={(e) => handleCatBtn(e, cat)}
                            // selected={selectedCategoryIndex === cat.idNodo}
                            selected={false}
                            title={cat.titolo}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {selectedBtns.cat && !selectedBtns.sub && (
              <>
                {isLoading ? (
                  <SkeletonCatalogo />
                ) : (
                  <div className={styles["view"]}>
                    <div
                      className={styles["macro-view"]}
                      style={{ flexDirection: "column", gap: "15px" }}
                    >
                      {dataSubcategories
                        ?.sort((a, b) => a.titolo.localeCompare(b.titolo))
                        .map((cat, i) => (
                          <SelectorCategory
                            key={i}
                            index={i}
                            onClick={(e) => handleSubBtn(e, cat)}
                            // selected={selectedSubcategoryIndex === cat.idNodo}
                            selected={false}
                            title={cat.titolo}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {selectedBtns.sub && !selectedBtns.prod && (
              <>
                {isLoading ? (
                  <SkeletonCatalogo />
                ) : (
                  <div className={styles["view"]}>
                    <div
                      className={styles["macro-view"]}
                      style={{ flexDirection: "column", gap: "15px" }}
                    >
                      {dataProducts
                        ?.sort((a, b) => a.titolo.localeCompare(b.titolo))
                        .map((cat, i) => (
                          <SelectorProduct
                            key={i}
                            index={i}
                            onClick={(e) => handleProdBtn(e, cat)}
                            // selected={selectedProductIndex === cat.idNodo}
                            selected={false}
                            title={cat.titolo}
                            desc={cat.sottotitolo}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className={styles["view"]}>
              <div className={styles["macro-view"]}>
                {selectedBtns.prod && (
                  <ProductView
                    isLoading={isLoading}
                    data={dataProductDetail}
                    onClick={unselectProd}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Catalogo;
